import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { superadminAPI, permissionsAPI } from '../../services/api';
import {
  LayoutDashboard, BookOpen, ClipboardList, BarChart2, User, Users,
  School, Layers, HelpCircle, FileText, Settings, LogOut, ChevronLeft,
  ChevronRight, ChevronDown, BookMarked, Trophy, Calendar, PlusCircle, Database,
  TrendingUp, Building2, GraduationCap, Bell, CheckSquare, History, FileStack, Inbox, CreditCard, MapPin, Wallet, FileBarChart2, ShieldAlert, AlertCircle, Receipt, MessageCircle, LifeBuoy, MessageSquare, Smartphone, ShieldCheck, UserCog, Phone, Archive, UserPlus, Upload
} from 'lucide-react';
import { SITE_HOME } from '../../config/siteLinks';
// FileBarChart2 already imported above (reused for both the finance
// "Collections Report" nav item and this new WhatsApp Reports one).

// A short two-tone beep via the Web Audio API — no audio asset to ship or
// host, just an oscillator. Wrapped in try/catch since autoplay-audio
// policies can block it before the user's first interaction with the page.
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    playTone(880, 0, 0.15);
    playTone(1174, 0.15, 0.2);
  } catch {
    // Web Audio unavailable/blocked — the badge count still updates silently
  }
};

// Polls the Super Admin's unread support-ticket count — the Phase 2 "instant
// notification", approximated via polling rather than a push channel (per
// the earlier decision to avoid an unverified WebSocket path through IIS).
// Only polls for the OFFICIAL superadmin (no organization_id), matching the
// backend's officialOnly gate on this endpoint — a scoped superadmin would
// just get a 403 on every tick.
const useSupportUnreadCount = (enabled) => {
  const [count, setCount] = useState(0);
  const prevCountRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const poll = () => {
      superadminAPI.getSupportUnreadCount().then(res => {
        if (cancelled) return;
        const newCount = res.data.data.count;
        if (prevCountRef.current !== null && newCount > prevCountRef.current) {
          playNotificationSound();
        }
        prevCountRef.current = newCount;
        setCount(newCount);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [enabled]);

  return count;
};

// Maps the icon NAME strings the backend registry sends (config/menuRegistry.js
// — plain serializable data, can't hold a component reference) back to the
// actual lucide-react components already imported above for these 4 roles.
const ICON_MAP = {
  LayoutDashboard, BookOpen, ClipboardList, BarChart2, User, Users, Layers,
  FileText, BookMarked, Calendar, PlusCircle, TrendingUp, Building2,
  GraduationCap, CheckSquare, History, FileStack, CreditCard, MapPin, Wallet,
  FileBarChart2, ShieldAlert, AlertCircle, Receipt, Database, LifeBuoy,
  MessageCircle, Smartphone, MessageSquare, Archive, Upload,
};

// Turns the flat registry response (each item optionally tagged with
// group/groupLabel/groupIcon) into the same nested {group:true, children}
// shape the render logic below already expects — grouped items collapse
// into one collapsible entry, in first-occurrence order.
const buildNavFromRegistry = (items) => {
  const result = [];
  const groups = {};
  for (const item of items) {
    const Icon = ICON_MAP[item.icon] || User;
    if (item.group) {
      if (!groups[item.group]) {
        const g = { label: item.groupLabel || item.group, icon: ICON_MAP[item.groupIcon] || Icon, group: true, children: [] };
        groups[item.group] = g;
        result.push(g);
      }
      groups[item.group].children.push({ label: item.label, path: item.path, icon: Icon });
    } else {
      result.push({ label: item.label, path: item.path, icon: Icon });
    }
  }
  return result;
};

// Fetches the current user's OWN effective menu (registry filtered by
// their role's permissions) — the single source of truth shared with the
// Super Admin's Permissions screen, so a menu added there always appears
// here too with no separate sync step. Falls back to the given static
// array while loading (or if the fetch fails) rather than a blank sidebar.
const useRoleMenu = (role, fallback) => {
  const [navItems, setNavItems] = useState(fallback);
  useEffect(() => {
    if (!['admin', 'teacher', 'instructor', 'accountant', 'student'].includes(role)) return;
    let cancelled = false;
    permissionsAPI.getMyMenu().then(res => {
      if (!cancelled) setNavItems(buildNavFromRegistry(res.data.data));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [role]);
  return navItems;
};

// The multi-org "Users tab" sub-account equivalent of useRoleMenu — but
// unlike the 4 configurable roles above, MOST superadmin logins (the
// official account, and any legacy single-org scoped superadmin) are
// unrestricted and must keep rendering the exact static list they always
// have. `restricted: false` (the default while loading, and what the API
// returns for those two account kinds) means "ignore me, use the static
// list" — only `restricted: true` swaps in the filtered, permission-driven
// items for an actual Users-tab sub-account.
const useSuperadminMenu = (role) => {
  const [menu, setMenu] = useState({ restricted: false, items: [] });
  useEffect(() => {
    if (role !== 'superadmin') return;
    let cancelled = false;
    superadminAPI.getMySuperadminMenu().then(res => {
      if (!cancelled) setMenu(res.data.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [role]);
  return menu;
};

const studentNavItems = [
  { label: 'Dashboard', path: '/student/dashboard', icon: LayoutDashboard },
  { label: 'Assigned Tests', path: '/student/assigned-tests', icon: CheckSquare },
  { label: 'Subjects', path: '/student/subjects', icon: BookOpen },
  { label: 'Build Your Test', path: '/student/build-test', icon: PlusCircle },
  { label: 'Daily Quiz', path: '/student/daily-quiz', icon: Calendar },
  { label: 'My Results', path: '/student/results', icon: ClipboardList },
  { label: 'Performance', path: '/student/performance', icon: BarChart2 },
  { label: 'eBooks', path: '/student/ebooks', icon: BookMarked },
  { label: 'Old Question Papers', path: '/student/question-papers', icon: FileStack },
  { label: 'Profile', path: '/student/profile', icon: User },
];

const teacherNavItems = [
  { label: 'Dashboard', path: '/teacher/dashboard', icon: LayoutDashboard },
  { label: 'Create Test', path: '/teacher/create-test', icon: PlusCircle },
  { label: 'Custom Test', path: '/teacher/custom-test', icon: Layers },
  { label: 'My Tests', path: '/teacher/tests', icon: ClipboardList },
  { label: 'Students', path: '/teacher/students', icon: Users },
  { label: 'Reports', path: '/teacher/reports', icon: BarChart2 },
  { label: 'Schedule', path: '/teacher/schedule', icon: Calendar },
  { label: 'Analytics', path: '/teacher/analytics', icon: TrendingUp },
  { label: 'Attendance', path: '/teacher/attendance', icon: MapPin },
  { label: 'Profile', path: '/teacher/profile', icon: User },
];

// Curriculum and the Question Bank are managed centrally by Maximuz Academy
// (superadmin-only, shared across every organization) — a customer's own
// admin doesn't create or maintain that content, so those sections aren't
// part of their panel.
const adminNavItems = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { label: 'Schools', path: '/admin/schools', icon: Building2 },
  { label: 'Teachers', path: '/admin/teachers', icon: GraduationCap },
  { label: 'Students', path: '/admin/students', icon: Users },
  { label: 'Batches', path: '/admin/batches', icon: Layers },
  { label: 'Schedule', path: '/admin/schedule', icon: Calendar },
  { label: 'Tests', path: '/admin/tests', icon: BookMarked },
  { label: 'Analytics', path: '/admin/analytics', icon: TrendingUp },
  { label: 'Reports', path: '/admin/reports', icon: FileText },
  { label: 'Billing', path: '/admin/billing', icon: CreditCard },
  { label: 'Attendance', path: '/admin/attendance', icon: MapPin },
  { label: 'Accountants', path: '/admin/accountants', icon: Wallet },
  // Same finance pages the accountant themselves uses — lets the admin
  // monitor billed vs. collected, see who hasn't paid, collect a fee
  // directly, and is the only role that can act on a correction request.
  // Collapsed into one "Accounting" group (see Sidebar's group-rendering
  // logic below) instead of five flat top-level links, per user request.
  {
    label: 'Accounting', icon: Wallet, group: true,
    children: [
      { label: 'Fee Collections', path: '/admin/finance/students', icon: Wallet },
      { label: 'Collections Report', path: '/admin/finance/reports', icon: FileBarChart2 },
      { label: 'Receipts', path: '/admin/finance/receipts', icon: Receipt },
      { label: 'Pending Dues', path: '/admin/finance/pending-dues', icon: AlertCircle },
      { label: 'Payment Corrections', path: '/admin/finance/corrections', icon: ShieldAlert },
    ],
  },
  { label: 'Activity Log', path: '/admin/audit-log', icon: History },
  { label: 'Profile', path: '/admin/profile', icon: User },
];

// Deliberately finance-only — never exam, attendance, or curriculum data, per
// the accountant role's whole purpose. Enforced server-side too (see
// backend/src/routes/finance.js's authorize list), this is just the nav.
const accountantNavItems = [
  { label: 'Dashboard', path: '/accountant/dashboard', icon: LayoutDashboard },
  { label: 'Fee Structures', path: '/accountant/fee-structures', icon: Layers },
  { label: 'Student Fees', path: '/accountant/students', icon: Users },
  { label: 'Collections Report', path: '/accountant/reports', icon: FileBarChart2 },
  { label: 'Receipts', path: '/accountant/receipts', icon: Receipt },
  { label: 'Pending Dues', path: '/accountant/pending-dues', icon: AlertCircle },
  { label: 'Corrections', path: '/accountant/corrections', icon: ShieldAlert },
  { label: 'Profile', path: '/accountant/profile', icon: User },
];

// Per explicit request: exact parity with teacherNavItems — same items,
// same order, same icons/labels. Every item below routes to the SAME reused
// teacher/ page as teacher does (App.js's /instructor/* route group),
// except Students — which stays on the instructor's own InstructorStudents.js
// rather than the teacher's TeacherStudents.js. That one deviation is
// necessary, not cosmetic: instructors add/edit/remove their own students
// directly (an explicit earlier requirement), while TeacherStudents.js is a
// read-only performance-viewing page — teachers never create students there.
// Swapping it for the teacher version would remove the instructor's only
// student-CRUD UI.
const instructorNavItems = [
  { label: 'Dashboard', path: '/instructor/dashboard', icon: LayoutDashboard },
  { label: 'My Courses', path: '/instructor/courses', icon: BookOpen },
  { label: 'Create Test', path: '/instructor/create-test', icon: PlusCircle },
  { label: 'Custom Test', path: '/instructor/custom-test', icon: Layers },
  { label: 'My Tests', path: '/instructor/tests', icon: ClipboardList },
  { label: 'Students', path: '/instructor/students', icon: Users },
  { label: 'Reports', path: '/instructor/reports', icon: BarChart2 },
  { label: 'Schedule', path: '/instructor/schedule', icon: Calendar },
  { label: 'Analytics', path: '/instructor/analytics', icon: TrendingUp },
  { label: 'Attendance', path: '/instructor/attendance', icon: MapPin },
  { label: 'Profile', path: '/instructor/profile', icon: User },
];

// A scoped superadmin (mapped to one organization) has no platform-wide
// Dashboard/Billing to browse — just their one organization — but can still
// onboard new customer organizations ("Add Customer"), just without the
// full cross-organization customer list that stays official-only.
// Curriculum and the Question Bank are global/shared across every organization
// and superadmin-editable only, so every superadmin (official or scoped) gets
// these two links.
const superadminNavItems = (user) => (user?.organization_id ? [
  { label: 'My Organization', path: `/superadmin/customers/${user.organization_id}`, icon: Building2 },
  { label: 'Add Customer', path: '/superadmin/customers', icon: Building2 },
  {
    label: 'Curriculum', icon: BookOpen, group: true,
    children: [
      { label: 'Curriculum', path: '/superadmin/curriculum', icon: BookOpen },
      { label: 'Question Bank 1', path: '/superadmin/questions', icon: Database },
      { label: 'Question Bank 2', path: '/superadmin/questions-2', icon: Database },
      { label: 'Curriculum Analytics', path: '/superadmin/curriculum-analytics', icon: TrendingUp },
      { label: 'Archived Data', path: '/superadmin/archive', icon: Archive },
    ],
  },
  { label: 'eBooks', path: '/superadmin/ebooks', icon: BookMarked },
  { label: 'Old Question Papers', path: '/superadmin/question-papers', icon: FileStack },
  { label: 'Profile', path: '/superadmin/profile', icon: User },
] : [
  { label: 'Dashboard', path: '/superadmin/dashboard', icon: LayoutDashboard },
  { label: 'Customers', path: '/superadmin/customers', icon: Building2 },
  { label: 'Instructors', path: '/superadmin/instructors', icon: UserCog },
  { label: 'Billing', path: '/superadmin/billing', icon: TrendingUp },
  { label: 'Inquiries', path: '/superadmin/inquiries', icon: Inbox },
  { label: 'Admissions', path: '/superadmin/admissions', icon: UserPlus },
  {
    label: 'Individual Students', icon: Users, group: true,
    children: [
      { label: 'Students', path: '/superadmin/individual-students', icon: Users },
      { label: 'Courses', path: '/superadmin/individual-courses', icon: BookOpen },
      { label: 'Payments', path: '/superadmin/individual-payments', icon: TrendingUp },
    ],
  },
  {
    label: 'Curriculum', icon: BookOpen, group: true,
    children: [
      { label: 'Curriculum', path: '/superadmin/curriculum', icon: BookOpen },
      { label: 'Question Bank 1', path: '/superadmin/questions', icon: Database },
      { label: 'Question Bank 2', path: '/superadmin/questions-2', icon: Database },
      { label: 'Curriculum Analytics', path: '/superadmin/curriculum-analytics', icon: TrendingUp },
      { label: 'Archived Data', path: '/superadmin/archive', icon: Archive },
    ],
  },
  { label: 'eBooks', path: '/superadmin/ebooks', icon: BookMarked },
  { label: 'Old Question Papers', path: '/superadmin/question-papers', icon: FileStack },
  { label: 'Support Tickets', path: '/superadmin/support/tickets', icon: LifeBuoy },
  {
    label: 'WhatsApp', icon: MessageCircle, group: true,
    children: [
      { label: 'Dashboard', path: '/superadmin/whatsapp-dashboard', icon: MessageCircle },
      { label: 'Accounts', path: '/superadmin/whatsapp-accounts', icon: Smartphone },
      { label: 'Numbers', path: '/superadmin/whatsapp-numbers', icon: Phone },
      { label: 'Reports', path: '/superadmin/whatsapp-reports', icon: FileBarChart2 },
      { label: 'Templates', path: '/superadmin/whatsapp-templates', icon: MessageSquare },
      { label: 'Test', path: '/superadmin/whatsapp-test', icon: MessageCircle },
    ],
  },
  { label: 'Users', path: '/superadmin/users', icon: UserCog },
  { label: 'Permissions', path: '/superadmin/permissions', icon: ShieldCheck },
  { label: 'Activity Log', path: '/superadmin/activity-log', icon: History },
  { label: 'Profile', path: '/superadmin/profile', icon: User },
]);

// Static fallbacks — rendered while the registry-driven menu is loading (or
// if that fetch fails), so the sidebar is never blank. Once useRoleMenu's
// fetch resolves, these are replaced by the permission-filtered result.
const staticFallbackByRole = {
  student: studentNavItems,
  teacher: teacherNavItems,
  admin: adminNavItems,
  accountant: accountantNavItems,
  // instructor IS part of the RBAC system now, so this is a genuine loading
  // fallback like the others — replaced by the permission-filtered menu once
  // useRoleMenu resolves.
  instructor: instructorNavItems,
};

const roleColors = {
  student: {
    bg: 'from-primary-700 to-primary-900',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-primary-500',
  },
  teacher: {
    bg: 'from-emerald-700 to-emerald-900',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-emerald-500',
  },
  admin: {
    bg: 'from-slate-800 to-slate-950',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-indigo-500',
  },
  superadmin: {
    bg: 'from-violet-800 to-violet-950',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-violet-500',
  },
  accountant: {
    bg: 'from-teal-800 to-teal-950',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-teal-500',
  },
  instructor: {
    bg: 'from-emerald-800 to-emerald-950',
    active: 'bg-white/20',
    hover: 'hover:bg-white/10',
    accent: 'bg-emerald-500',
  },
};

const Sidebar = ({ role, mobileOpen, onMobileClose, collapsed, onToggleCollapse }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const roleMenu = useRoleMenu(role, staticFallbackByRole[role] || []);
  const superadminMenu = useSuperadminMenu(role);
  const navItems = role === 'superadmin'
    ? (superadminMenu.restricted ? buildNavFromRegistry(superadminMenu.items) : superadminNavItems(user))
    : roleMenu;
  const colors = roleColors[role] || roleColors.student;
  const supportUnreadCount = useSupportUnreadCount(role === 'superadmin' && !user?.organization_id);

  // Collapsible groups (e.g. admin's "Accounting"): expanded state is tied
  // to the current route, not just click-toggling — landing directly on a
  // group's page (via URL or refresh) auto-expands it so the active item is
  // visible, and navigating to anything outside the group collapses it.
  // Toggling the group header itself (no route change) is handled by the
  // separate onClick below.
  const [expandedGroup, setExpandedGroup] = useState(null);
  useEffect(() => {
    const activeGroup = navItems.find((item) => item.group && item.children.some((c) => location.pathname.startsWith(c.path)));
    setExpandedGroup(activeGroup ? activeGroup.label : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    window.location.href = SITE_HOME;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const userName = user?.name || user?.username || 'User';
  const userRole = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User';

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-50 flex flex-col
          bg-gradient-to-b ${colors.bg}
          transition-all duration-300 shadow-2xl
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          <div className={`${colors.accent} rounded-xl p-2 flex-shrink-0`}>
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              {user?.organization_name ? (
                <>
                  <h1 className="text-white font-bold text-sm leading-tight truncate" title={user.organization_name}>
                    {user.organization_name}
                  </h1>
                  <p className="text-white/60 text-xs">Academy</p>
                </>
              ) : (
                <h1 className="text-white font-bold text-sm leading-tight">Academy</h1>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navItems.map((item) => {
            if (item.group) {
              const GroupIcon = item.icon;
              const isChildActive = item.children.some((c) => location.pathname.startsWith(c.path));
              const isExpanded = expandedGroup === item.label;

              // Icon-only (fully collapsed) sidebar has no room for an
              // expandable submenu — link straight to the group's first
              // page instead of a dead-end toggle button.
              if (collapsed) {
                return (
                  <NavLink
                    key={item.label}
                    to={item.children[0].path}
                    onClick={onMobileClose}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all
                      ${isChildActive ? `${colors.active} text-white` : `text-white/60 ${colors.hover} hover:text-white`}`}
                    title={item.label}
                  >
                    <GroupIcon className="h-5 w-5 flex-shrink-0" />
                  </NavLink>
                );
              }

              return (
                <div key={item.label} className="mb-1">
                  <button
                    type="button"
                    onClick={() => setExpandedGroup((prev) => (prev === item.label ? null : item.label))}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                      ${isChildActive ? `${colors.active} text-white` : `text-white/60 ${colors.hover} hover:text-white`}`}
                  >
                    <GroupIcon className="h-5 w-5 flex-shrink-0" />
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                  </button>
                  {isExpanded && (
                    <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={onMobileClose}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all
                              ${isActive ? `${colors.active} text-white` : `text-white/60 ${colors.hover} hover:text-white`}`
                            }
                          >
                            <ChildIcon className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">{child.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const Icon = item.icon;
            const badgeCount = item.path === '/superadmin/support/tickets' ? supportUnreadCount : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onMobileClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-all
                  ${isActive
                    ? `${colors.active} text-white`
                    : `text-white/60 ${colors.hover} hover:text-white`
                  }`
                }
                title={collapsed ? item.label : undefined}
              >
                <span className="relative flex-shrink-0">
                  <Icon className="h-5 w-5" />
                  {badgeCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="border-t border-white/10 p-3">
          {!collapsed && (
            <div className="flex items-center gap-3 px-2 py-2 mb-2">
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">{getInitials(userName)}</span>
              </div>
              <div className="overflow-hidden">
                <p className="text-white text-sm font-medium truncate">{userName}</p>
                <p className="text-white/50 text-xs">{userRole}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all`}
            title={collapsed ? 'Logout' : undefined}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>

        {/* Collapse toggle (desktop only) */}
        <button
          onClick={onToggleCollapse}
          className="absolute -right-3 top-20 bg-white rounded-full p-1 shadow-md border border-slate-200 hover:bg-slate-50 hidden lg:flex"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-600" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          )}
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
